import { isEmpty, omit } from "lodash-es";
import { useCallback } from "react";
import { useToken } from "../../../server/auth/use-token";
import { useSupabaseLoader } from "../../../server/supabase/hooks";
import { convertEmailSvgImage } from "../../../utils/convert-svg-image";
import { useAsyncStateSupabaseCallback } from "../../../utils/hooks/use-async-state-callback";
import { convertToMjmlString } from "../../message/editor/utils/convert-to-mjml/convert-to-mjml";
import { convertSettingsToMjml } from "../../message/editor/utils/convert-to-mjml/layout";
import { SupabaseEmailDesignService } from "../server/supabase-email-design-service";
import { DEFAULT_EMAIL_DESIGN_NAME } from "../types/email-design-type";
import { makeHeaderSerializedState } from "../util";

import type { UseAsyncStateCallback } from "../../../utils/hooks/use-async-state-callback";
import type { UUID } from "../../../utils/uuid";
import type { EmailDesign } from "../server/supabase-email-design-service";
import type { EmailDesignSchema } from "../types/email-design-type";

/** `preloaded` is for a caller handed the design already — a reviewer's preview — and skips the read. */
export function useEmailDesign(
  id?: UUID,
  preloaded?: { email_design: EmailDesign | null },
): ReturnType<typeof useSupabaseLoader<EmailDesign | null>> {
  return useSupabaseLoader(
    async ({ supabase, account_id }) => {
      if (preloaded) return preloaded.email_design;

      const { data, error } = await new SupabaseEmailDesignService(supabase).maybeGet(
        id ?? account_id,
      );

      if (error) throw new Error(error.message);

      return data;
    },
    [id, preloaded],
  );
}

export function useEmailDesigns(): ReturnType<typeof useSupabaseLoader<EmailDesign[]>> {
  return useSupabaseLoader(async ({ supabase, account_id }) => {
    const { data: designs, error: designError } = await new SupabaseEmailDesignService(
      supabase,
    ).getAll(account_id, {
      filter: [{ key: "deleted_at", operator: "is", value: null }],
      order: [{ column: "name" }],
    });

    if (designError || !designs) throw new Error(designError?.message ?? "Failed to load designs");

    designs.forEach((design) => {
      if (design.header?.logo && design.header?.logo?.version !== 2) {
        design.header.logo.proportionOfMaxWidth *= 0.33;
        design.header.logo.version = 2;
      }
    });
    designs.forEach((design) => {
      if (design.text_settings && !design.text_settings?.link_color) {
        design.text_settings.link_color = "#0000EE";
      }
    });
    // if the created template has just the single padding then assume it is the old template
    // and set the new paddings to the same value.
    // Upon the next save, the new paddings will be saved to the DB and the single padding will be removed.
    designs.forEach((design) => {
      if (typeof design?.header?.padding === "number") {
        design.header.paper_padding_side = design.header.padding || 0;
        design.header.paper_padding_bottom = design.header.padding || 0;
        design.header.paper_padding_top = design.header.padding || 0;
        delete design.header.padding;
      }
    });

    // we have removed column spacing and repalced it with individual (optionla) paddings
    // This only adds so works for FF on and off.
    designs.forEach((design) => {
      // if column_spacing_type is spacing then first pass is halve and set to X paddings, with Y padding to 0
      // and set column_individual_padding true.
      if (
        design.layout_settings?.column_spacing_type === "spacing" &&
        typeof design.layout_settings?.column_spacing === "number" &&
        // If it's already been converted, don't do it again
        typeof design.layout_settings?.column_individual_padding === "undefined"
      ) {
        design.layout_settings.column_individual_padding = true;

        const xPadding = design.layout_settings.column_spacing / 2 || 0;
        const yPadding = 0;
        design.layout_settings.column_padding_left = xPadding;
        design.layout_settings.column_padding_right = xPadding;
        design.layout_settings.column_padding_top = yPadding;
        design.layout_settings.column_padding_bottom = yPadding;

        design.layout_settings.column_padding = xPadding; // selected as the highest value
      }

      // if column_spacing_type is padding then set the value to column_padding and set column_individual_padding to false
      if (
        design.layout_settings?.column_spacing_type === "padding" &&
        typeof design.layout_settings?.column_padding === "number"
      ) {
        design.layout_settings.column_individual_padding = false;
        // uses the current value of column_padding
        const padding = design.layout_settings.column_padding || 0;
        design.layout_settings.column_padding_left = padding;
        design.layout_settings.column_padding_right = padding;
        design.layout_settings.column_padding_top = padding;
        design.layout_settings.column_padding_bottom = padding;
      }

      // This is a shortcut for all being zero, so set universal padding to 0
      if (design.layout_settings?.column_spacing_type === "none") {
        design.layout_settings.column_individual_padding = false;
        design.layout_settings.column_padding = 0;
        design.layout_settings.column_padding_left = 0;
        design.layout_settings.column_padding_right = 0;
        design.layout_settings.column_padding_top = 0;
        design.layout_settings.column_padding_bottom = 0;
      }
    });

    return designs;
  }, []);
}

export function useEmailDesignActions(existing?: EmailDesign): {
  save: UseAsyncStateCallback<Partial<EmailDesign> | undefined, [UUID, EmailDesignSchema]>;
  archive: UseAsyncStateCallback<boolean | undefined, [UUID]>;
} {
  const getToken = useToken();

  const convertSvgImage = useCallback(
    async (url: string | undefined) => {
      if (!url) return "";

      const logoSrc = await convertEmailSvgImage(url, getToken, "email");

      if (!logoSrc?.success) {
        throw new Error("Failed to convert SVG in email header.");
      }

      return logoSrc.url;
    },
    [getToken],
  );

  const save = useAsyncStateSupabaseCallback(
    async ({ supabase, account_id }, id: UUID, updates: EmailDesignSchema) => {
      const serializedHeader =
        updates.header && (await makeHeaderSerializedState(updates.header, convertSvgImage));
      const serializedFooter = updates.footer;

      const { is_default, name: newName, header, ...rest } = updates;

      const name = isEmpty(newName) ? existing?.name || DEFAULT_EMAIL_DESIGN_NAME : newName;

      let footerPaperSize: number | undefined = undefined;
      if (updates.layout_settings && updates.footer_layout_settings) {
        footerPaperSize =
          updates.layout_settings.max_paper_width -
          updates.footer_layout_settings.paper_padding_side * 2;
      }

      let headerPaperSize: number | undefined = undefined;
      if (updates.layout_settings && header) {
        headerPaperSize = updates.layout_settings.max_paper_width - 2 * header.paper_padding_side;
      }

      // Styleable column data will always be saved now, but will be ignored if the flag is off

      const upsertData: Partial<EmailDesign> = {
        ...omit(rest, "used_merge_fields"),
        header,
        name,
        id,
        styles_mjml: convertSettingsToMjml(updates, true),
        header_mjml: serializedHeader
          ? convertToMjmlString(
              serializedHeader,
              account_id,
              "header",
              headerPaperSize,
              updates.header?.hide_image_url_from_notification,
            )
          : null,
        footer_mjml: serializedFooter?.root
          ? convertToMjmlString(serializedFooter, account_id, "footer", footerPaperSize)
          : null,
        footer: serializedFooter,
      };

      const service = new SupabaseEmailDesignService(supabase);

      const { data, error } = await service.upsert(upsertData);

      if (error) throw new Error(error.message);

      if (is_default && !existing?.is_default && upsertData.id)
        await service.setDefault(upsertData.id);

      return data;
    },
    [existing, convertSvgImage],
  );

  const archive = useAsyncStateSupabaseCallback(
    async ({ supabase }, new_email_design_id: UUID) => {
      if (!existing) throw new Error("No design to archive");

      const { data, error } = await new SupabaseEmailDesignService(supabase).archive(
        existing.id,
        new_email_design_id,
      );

      if (error) throw new Error(error.message);

      return data;
    },
    [existing],
  );

  return { save, archive };
}
